import { useState } from "react";
import type React from "react";
import { NavLink, Link } from "@remix-run/react";
import type { LinksFunction } from "@vercel/remix";
import { SignedIn, SignedOut, UserButton, SignUpButton } from "@clerk/remix";
import Footer from "~/components/Footer";
import StructuredData from "~/components/StructuredData";
import BlackFridayBanner from "~/components/BlackFridayBanner";

export const links: LinksFunction = () => [
  { rel: "canonical", href: "https://cloudexamhub.com/checklist" },
];

// Add metadata export for Remix
export function meta() {
  return [
    { title: "AWS Cloud Practitioner Study Checklist 2026" },
    {
      name: "description",
      content:
        "Interactive 4-week study checklist for AWS Cloud Practitioner (CLF-C02) exam preparation. Track your progress through key exam topics and preparation milestones.",
    },
    {
      name: "keywords",
      content:
        "AWS Cloud Practitioner, study checklist, exam preparation, certification study guide, AWS certification, CLF-C02",
    },
    { property: "og:title", content: "AWS Cloud Practitioner Study Checklist 2026" },
    {
      property: "og:description",
      content:
        "Interactive 4-week study checklist for AWS Cloud Practitioner (CLF-C02) exam prep. Track your progress step by step.",
    },
    { property: "og:type", content: "article" },
    { property: "og:url", content: "https://cloudexamhub.com/checklist" },
    { property: "og:image", content: "https://cloudexamhub.com/cloud-exam-hub-full-logo-white-background.png" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:image", content: "https://cloudexamhub.com/cloud-exam-hub-full-logo-white-background.png" },
    { name: "twitter:title", content: "AWS Cloud Practitioner Study Checklist 2026" },
    {
      name: "twitter:description",
      content:
        "Interactive 4-week study checklist for AWS Cloud Practitioner (CLF-C02) exam prep. Track your progress step by step.",
    },
  ];
}

const StudyChecklist = () => {
  const [completed, setCompleted] = useState<number[]>([]);

  const toggleCompletion = (index: number) => {
    if (completed.includes(index)) {
      setCompleted(completed.filter((item) => item !== index));
    } else {
      setCompleted([...completed, index]);
    }
  };

  // Plain-text items used for schema and progress count
  const checklistSections = [
    {
      title: "Initial Preparation",
      schemaItems: [
        "Understand the AWS Certified Cloud Practitioner Exam Format (multiple choice/multiple response, 90 minutes, foundational level).",
        "Review the exam guide from AWS to identify key domains (Cloud Concepts, Security and Compliance, Cloud Technology and Services, Billing, Pricing and Support).",
      ],
      displayItems: [
        "Understand the AWS Certified Cloud Practitioner Exam Format (multiple choice/multiple response, 90 minutes, foundational level).",
        "Review the exam guide from AWS to identify key domains (Cloud Concepts, Security and Compliance, Cloud Technology and Services, Billing, Pricing and Support).",
      ] as React.ReactNode[],
    },
    {
      title: "Week 1: Watch and Learn",
      schemaItems: [
        "Watch the first half of the FreeCodeCamp AWS Certified Cloud Practitioner Course on YouTube (7 hours or 3.5 hours on 2x speed).",
        "Create an AWS Free Tier account and explore the Management Console. Work along with the videos and play with console to better understand concepts.",
        "Register on Cloud Exam Hub and take an initial diagnostic test to identify weak areas.",
        "Schedule your exam.",
      ],
      displayItems: [
        "Watch the first half of the FreeCodeCamp AWS Certified Cloud Practitioner Course on YouTube (7 hours or 3.5 hours on 2x speed).",
        "Create an AWS Free Tier account and explore the Management Console. Work along with the videos and play with console to better understand concepts.",
        <span key="register">
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2 cursor-pointer">
                Register on Cloud Exam Hub
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <Link to="/user-home" className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2">
              Go to Cloud Exam Hub
            </Link>
          </SignedIn>
          {" "}and take an initial diagnostic test to identify weak areas.
        </span>,
        "Schedule your exam.",
      ] as React.ReactNode[],
    },
    {
      title: "Week 2: Practice and Explore",
      schemaItems: [
        "Watch the second half of the FreeCodeCamp AWS Certified Cloud Practitioner Course on YouTube (7 hours or 3.5 hours on 2x speed).",
        "Continue to explore the Management Console and practice using core AWS services.",
        "Take another practice test on Cloud Exam Hub to continue to identify weak areas.",
      ],
      displayItems: [
        "Watch the second half of the FreeCodeCamp AWS Certified Cloud Practitioner Course on YouTube (7 hours or 3.5 hours on 2x speed).",
        "Continue to explore the Management Console and practice using core AWS services.",
        <span key="practice2">
          Take another practice test on{" "}
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2 cursor-pointer">
                Cloud Exam Hub
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <Link to="/user-home" className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2">
              Cloud Exam Hub
            </Link>
          </SignedIn>
          {" "}to continue to identify weak areas.
        </span>,
      ] as React.ReactNode[],
    },
    {
      title: "Week 3: Focused Revision",
      schemaItems: [
        "Re-watch complex sections of the FreeCodeCamp video as needed.",
        "Take four or more practice tests on Cloud Exam Hub.",
        "Review incorrect answers and revisit related topics in your notes or AWS documentation.",
      ],
      displayItems: [
        "Re-watch complex sections of the FreeCodeCamp video as needed.",
        <span key="practice3">
          Take four or more practice tests on{" "}
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2 cursor-pointer">
                Cloud Exam Hub
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <Link to="/user-home" className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2">
              Cloud Exam Hub
            </Link>
          </SignedIn>
          .
        </span>,
        "Review incorrect answers and revisit related topics in your notes or AWS documentation.",
      ] as React.ReactNode[],
    },
    {
      title: "Week 4: Final Review and Practice",
      schemaItems: [
        "Take four or more practice tests on Cloud Exam Hub to confirm readiness.",
        "Practice time management to complete 65 questions in 90 minutes.",
        "Revisit critical topics and review cheat sheets and flashcards.",
        "Spend time on weak areas identified in previous tests.",
        "Ensure you meet all prerequisites for the exam (valid ID, exam scheduling, stable internet connection if remote).",
      ],
      displayItems: [
        <span key="practice4">
          Take four or more practice tests on{" "}
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2 cursor-pointer">
                Cloud Exam Hub
              </button>
            </SignUpButton>
          </SignedOut>
          <SignedIn>
            <Link to="/user-home" className="text-blue-600 hover:text-blue-700 font-semibold underline underline-offset-2">
              Cloud Exam Hub
            </Link>
          </SignedIn>
          {" "}to confirm readiness.
        </span>,
        "Practice time management to complete 65 questions in 90 minutes.",
        "Revisit critical topics and review cheat sheets and flashcards.",
        "Spend time on weak areas identified in previous tests.",
        "Ensure you meet all prerequisites for the exam (valid ID, exam scheduling, stable internet connection if remote).",
      ] as React.ReactNode[],
    },
    {
      title: "Exam Day Preparation",
      schemaItems: [
        "Relax and get plenty of rest before the exam day.",
        "Prepare your workspace for remote exams or plan your route to the test center.",
      ],
      displayItems: [
        "Relax and get plenty of rest before the exam day.",
        "Prepare your workspace for remote exams or plan your route to the test center.",
      ] as React.ReactNode[],
    },
    {
      title: "Post-Exam",
      schemaItems: [
        "Reflect on performance and document lessons learned.",
        "Celebrate your success upon passing the exam!",
      ],
      displayItems: [
        "Reflect on performance and document lessons learned.",
        "Celebrate your success upon passing the exam!",
      ] as React.ReactNode[],
    },
  ];

  // Flatten plain-text items for schema and progress count
  const allSchemaItems = checklistSections.flatMap((section) => section.schemaItems);
  const totalItemCount = checklistSections.reduce((acc, s) => acc + s.displayItems.length, 0);

  // Schema for SEO — uses plain strings only
  const checklistSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "AWS Cloud Practitioner Certification Study Checklist",
    description:
      "A comprehensive checklist to prepare for the AWS Cloud Practitioner certification exam",
    step: allSchemaItems.map((item, index) => ({
      "@type": "HowToStep",
      position: index + 1,
      name: item,
      text: item,
    })),
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://cloudexamhub.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "AWS Cloud Practitioner Certification Study Checklist",
        item: "https://cloudexamhub.com/checklist",
      },
    ],
  };

  return (
    <div className="flex flex-col min-h-screen">
      <StructuredData data={checklistSchema} />
      <StructuredData data={breadcrumbSchema} />
      <BlackFridayBanner />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b px-4 sm:px-8 lg:px-12 h-16 sm:h-14 flex items-center">
        <NavLink to="/" className="flex items-center justify-center">
          <img
            src="/cloud-check.png"
            alt="Cloud Exam Hub"
            className="h-8 w-8 sm:h-6 sm:w-6"
          />
          <span className="ml-2 font-bold text-lg sm:text-xl truncate">
            Cloud Exam Hub
          </span>
        </NavLink>
        <nav className="ml-auto flex gap-4 sm:gap-4 items-center">
          <SignedIn>
            <UserButton afterSignOutUrl="/" />
          </SignedIn>
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md text-sm transition-colors">
                Get Practice Exams
              </button>
            </SignUpButton>
          </SignedOut>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="w-full py-20 md:py-28 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="text-center text-white">
            <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl mb-6">
              AWS Cloud Practitioner Study Checklist 2026
            </h1>
            <h2 className="max-w-[800px] mx-auto text-gray-200 text-lg md:text-xl">
              Track your 4-week exam preparation progress step by step
            </h2>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Study Progress Tracker
              </h2>
              <p className="text-xl text-gray-700 mb-4">
                This checklist is designed to help you prepare for the AWS Cloud
                Practitioner certification exam. It provides a structured plan
                to help you practice effectively. Adjust based on your
                experience and time available.
              </p>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-blue-700">
                  Completed: {completed.length} of {totalItemCount} tasks
                </p>
                <div className="w-full bg-blue-200 rounded-full h-2.5 mt-2">
                  <div
                    className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                    style={{
                      width: `${(completed.length / totalItemCount) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              {checklistSections.map((section, sectionIndex) => (
                <div key={sectionIndex} className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">
                    {section.title}
                  </h3>
                  <ul className="space-y-4">
                    {section.displayItems.map((item, itemIndex) => {
                      const globalIndex =
                        checklistSections
                          .slice(0, sectionIndex)
                          .reduce((acc, s) => acc + s.displayItems.length, 0) +
                        itemIndex;

                      return (
                        <li
                          key={itemIndex}
                          className="flex items-start p-4 bg-white rounded-lg hover:bg-gray-100 transition-colors min-h-[4.5rem]"
                        >
                          <input
                            type="checkbox"
                            className="w-5 h-5 mt-1 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
                            checked={completed.includes(globalIndex)}
                            onChange={() => toggleCompletion(globalIndex)}
                            id={`task-${globalIndex}`}
                          />
                          <label
                            htmlFor={`task-${globalIndex}`}
                            className={`ml-3 text-gray-700 ${
                              completed.includes(globalIndex)
                                ? "line-through text-gray-500"
                                : ""
                            }`}
                          >
                            {item}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Bottom Conversion Section */}
      <section className="bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400 py-16 px-4">
        <div className="max-w-3xl mx-auto text-center text-white">
          <h2 className="text-3xl font-bold mb-4">The #1 Tool on This Checklist</h2>
          <p className="text-lg text-blue-100 mb-8">
            The checklist recommends Cloud Exam Hub multiple times — and for good reason. It&apos;s the fastest way to identify your weak spots and build exam confidence with 500+ up-to-date CLF-C02 practice questions.
          </p>
          <div className="bg-white/10 rounded-xl p-6 mb-8 grid sm:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-2xl font-bold">500+</div>
              <div className="text-blue-100">Practice questions</div>
            </div>
            <div>
              <div className="text-2xl font-bold">Unlimited</div>
              <div className="text-blue-100">Exam simulations</div>
            </div>
            <div>
              <div className="text-2xl font-bold">14-day</div>
              <div className="text-blue-100">Money-back guarantee</div>
            </div>
          </div>
          <SignedOut>
            <SignUpButton mode="modal">
              <button className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-lg text-xl transition-colors shadow-lg">
                Get Access for $11.99 →
              </button>
            </SignUpButton>
            <p className="mt-3 text-blue-200 text-sm">One-time payment · No subscription · Was $19.99</p>
          </SignedOut>
          <SignedIn>
            <Link
              to="/user-home"
              className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-10 py-4 rounded-lg text-xl transition-colors shadow-lg inline-block"
            >
              Go to Practice Exams →
            </Link>
          </SignedIn>
        </div>
      </section>

      {/* Related Resources */}
      <section className="container mx-auto px-4 py-10 max-w-3xl">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to="/certification-faqs" className="text-blue-600 hover:text-blue-700 font-medium">
                AWS Cloud Practitioner Certification FAQs 2026
              </Link>
            </li>
            <li>
              <Link to="/glossary" className="text-blue-600 hover:text-blue-700 font-medium">
                AWS Cloud Practitioner Glossary of Key Terms
              </Link>
            </li>
            <li>
              <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
                Cloud Exam Hub — Practice Exams
              </Link>
            </li>
          </ul>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudyChecklist;
